import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { digOut } from './utils';
import { Collection } from './collection';
import { IComponentClassName } from './types';
import { filter, map, tap } from 'rxjs/operators';

@Injectable()
export class ComponentPrototype implements OnDestroy {
  public readonly classNames$: Collection<IComponentClassName> = new Collection<IComponentClassName>({
    indexBy: 'name',
    allowMultiselect: true,
  });

  public readonly componentSubscriptions: Subscription = new Subscription();

  public readonly namedSubscriptions: Map<any, Subscription> = new Map<any, Subscription>();

  public registerSubscriptions = (...args: Subscription[]): this => {
    Array.from(args).forEach(s => s instanceof Subscription && this.componentSubscriptions.add(s));
    return this;
  };

  public registerNamedSubscription = (name: any, subscription: Subscription): this => {
    this.clearNamedSubscription(name).namedSubscriptions.set(name, subscription);
    return this;
  };

  public clearNamedSubscription = (name: any): this => {
    const subscription = this.namedSubscriptions.get(name);
    subscription?.unsubscribe();
    this.namedSubscriptions.delete(name);

    return this;
  };

  public hasNamedSubscription = (name: any): boolean => {
    return this.namedSubscriptions.has(name);
  };

  public clearNamedSubscriptions = (): this => {
    Array.from(this.namedSubscriptions.keys()).forEach(name => this.clearNamedSubscription(name));
    return this;
  };

  public clearSubscriptions = (): this => {
    this.componentSubscriptions.unsubscribe();
    return this;
  };

  public toggleClassName = (className: any, state: boolean = null): this => {
    const { classNames$ } = this;

    !classNames$.hasItem(className) && classNames$.add({ name: className });

    classNames$.toggle(className, state);

    return this;
  };

  public hasClassName = (className: any): boolean => {
    return this.classNames$.isSelected(className);
  };

  public onClassChange = (className: any, skipFirst: boolean = false): Observable<boolean> => {
    const { classNames$, hasClassName } = this;
    let cached = skipFirst ? hasClassName(className) : null;

    return classNames$.onSelect().pipe(
      map(() => hasClassName(className)),
      filter(current => cached !== current),
      tap(current => (cached = current))
    );
  };

  public getActiveSubscriptionsLength = (): number => {
    const unnamedSubscriptions = digOut(this.componentSubscriptions, '_subscriptions');

    return unnamedSubscriptions?.length || 0;
  };

  public get className(): string {
    return this.classNames$.map(c => c.name).join(' ');
  }

  ngOnDestroy(): void {
    this.clearNamedSubscriptions().clearSubscriptions();
  }
}
